/* eslint-disable max-len */
// ==============================================
// ==================Import======================
// ==============================================

// ==================REACT & HOOK===================
import { Link } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

// ==================AG GRID===================
import { AgGridReact } from 'ag-grid-react';

// ==================Style&IMG===================
import './style.scss';

// ==================Actions===================
import {
  saveMultipleGridRows, setSaveMultipleStationSelected, setSaveStringGridSelect,
  setTimeseries,
} from '../../../actions/multipleMetadata';
import { setBtnExport } from '../../../actions/menuButton';

// define column for aggrid
const columns = [
  {
    field: 'marker',
    headerName: 'Marker',
    minWidth: 120,
    headerCheckboxSelection: true,
    checkboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    suppressMenu: true,
    floatingFilter: true,
    cellRenderer: (params) => <> <Link to={`/metadata/marker=${params.value}/description`}>{params.value}</Link></>,
  },
  {
    field: 'site',
    minWidth: 50,
    headerName: 'Site Name',
    suppressMenu: true,
    floatingFilter: true,
  },
  {
    field: 'installDate',
    minWidth: 50,
    headerName: 'Install Date',
    filter: 'agDateColumnFilter',
  },
  {
    field: 'endDate',
    minWidth: 50,
    headerName: 'End Date',
  },
  {
    field: 'lat',
    minWidth: 50,
    headerName: 'Latitude (°D)',
    filter: 'agNumberColumnFilter',
  },
  {
    field: 'lon',
    minWidth: 50,
    headerName: 'Longitude (°D)',
    filter: 'agNumberColumnFilter',
  },
  {
    field: 'alt',
    minWidth: 50,
    headerName: 'Altitude (m)',
    filter: 'agNumberColumnFilter',
  },
  {
    field: 'network',
    minWidth: 50,
    headerName: 'Network',
    suppressMenu: true,
    floatingFilter: true,
  },
  {
    field: 'country',
    minWidth: 50,
    headerName: 'Country',
    suppressMenu: true,
    floatingFilter: true,
  },
  {
    field: 'lastReceiver',
    minWidth: 50,
    headerName: 'Last Receiver',
    suppressMenu: true,
    floatingFilter: true,
  },
  {
    field: 'lastAntenna',
    minWidth: 50,
    headerName: 'Last Antenna',
    suppressMenu: true,
    floatingFilter: true,
  },
  {
    field: 'lastRadome',
    minWidth: 50,
    headerName: 'Last Radome',
    suppressMenu: true,
    floatingFilter: true,
  },
  {
    field: 'dataAvailibility',
    minWidth: 50,
    headerName: 'Data Availibility (%)',
    type: 'currency',
  },
];

function MultiplemetadataGrid() {
  // =============HOOKS============
  const dispatch = useDispatch();
  const multipleGridRows = useSelector(
    (state) => state.multipleMetadata.dataMultiple.multipleGridRows,
  );
  const dataPercent = useSelector((state) => state.multipleMetadata.dataPercent);
  const multipleMetadataArray = useSelector(
    (state) => state.multipleMetadata.dataMultiple.multipleMetadataArray,
  );
  const multipleSelectedStationsFullData = useSelector(
    (state) => state.multipleMetadata.dataMultiple.fullDataSelectedStation,
  );

  const multipleStationsSelected = useSelector(
    (state) => state.multipleMetadata.dataMultiple.multipleStationsSelected,
  );

  const dataAPI = useSelector((state) => state.dataAPI.stations.list);

  const btnExport = useSelector((state) => state.menuButton.btnExport);

  const currencyFormatter = (params) => {
    const { value } = params;
    if (Number.isNaN(value)) {
      return '';
    }
    // eslint-disable-next-line prefer-template
    return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') + '%';
  };
  // ============== UseMemo ========================

  // to memoize default params of each column of aggrid lib
  const defaultColDef = useMemo(() => ({
    flex: 1,
    minWidth: 100,
    sortable: true,
    resizable: true,
  }), []);

  const columnTypes = useMemo(() => ({
    currency: {
      width: 150,
      valueFormatter: currencyFormatter,
    },
    shaded: {
      cellClass: 'shaded-class',
    },
  }), []);

  // ================= Refs =========================
  const gridRef = useRef();

  // ============== Function ========================

  // function allowing you to put the data in order and format it for the ag-grid library
  function rowsOfMultipleDataGrid() {
    const rowsMultipleDataGrid = [];
    let receiver = '';
    let antenna = '';
    let radome = '';
    let network = '';
    if (multipleSelectedStationsFullData.length !== 0) {
      for (let i = 0; i <= multipleSelectedStationsFullData.length - 1; i += 1) {
        for (let y = 0; y <= multipleSelectedStationsFullData[i].station_items.length - 1; y += 1) {
          if (multipleSelectedStationsFullData[i].station_items[y].date_to === null) {
            if (multipleSelectedStationsFullData[i].station_items[y].item.item_type.name === 'receiver') {
              receiver = multipleSelectedStationsFullData[i].station_items[y].item.item_attributes[0].value_varchar;
            }
            if (multipleSelectedStationsFullData[i].station_items[y].item.item_type.name === 'antenna') {
              antenna = multipleSelectedStationsFullData[i].station_items[y].item.item_attributes[0].value_varchar;
            }
            if (multipleSelectedStationsFullData[i].station_items[y].item.item_type.name === 'radome') {
              radome = multipleSelectedStationsFullData[i].station_items[y].item.item_attributes[0].value_varchar;
            }
          }
        }
        for (let z = 0; z <= multipleMetadataArray.length - 1; z += 1) {
          if (multipleMetadataArray.marker_long_name === multipleSelectedStationsFullData.marker_long_name) {
            network = multipleMetadataArray[i].network.name;
          }
        }
        if (multipleSelectedStationsFullData[i].data_percent !== undefined) {
          const dataAvailibilityToPush = multipleSelectedStationsFullData[i].data_percent.slice(0, -1);
          const dataGridRow = {
            id: i,
            marker: multipleSelectedStationsFullData[i].marker_long_name,
            site: multipleSelectedStationsFullData[i].name,
            lat: multipleSelectedStationsFullData[i].location.coordinates.lat,
            lon: multipleSelectedStationsFullData[i].location.coordinates.lon,
            alt: multipleSelectedStationsFullData[i].location.coordinates.altitude,
            installDate: multipleSelectedStationsFullData[i].date_from,
            endDate: multipleSelectedStationsFullData[i].date_to,
            country: multipleSelectedStationsFullData[i].location.city.state.country.name,
            dataAvailibility: Number(dataAvailibilityToPush),
            lastReceiver: receiver,
            lastAntenna: antenna,
            lastRadome: radome,
            network: network,
          };
          rowsMultipleDataGrid.push(dataGridRow);
        }
      }
    }
    dispatch(saveMultipleGridRows(rowsMultipleDataGrid));
  }

  // when event on grid we save the name and data marker checked
  const onSelectionChanged = useCallback((event) => {
    const rowSelected = event.api.getSelectedNodes();
    let rowSelectedString = '';
    rowSelected.forEach((el) => {
      const dataToPush = dataAPI.data.find((el2) => el.data.marker === el2.marker_long_name);
      rowSelectedString += `${dataToPush.marker},`;
    });
    if (rowSelectedString.length > 0) {
      rowSelectedString = rowSelectedString.slice(0, -1);
    }
    const arrayToBlob = [];

    const selectedMarker = rowSelectedString.split(',');

    selectedMarker.forEach((el) => {
      // eslint-disable-next-line eqeqeq
      const elToAdd = multipleSelectedStationsFullData.find((el2) => el2.marker == el);
      arrayToBlob.push(elToAdd);
    });
    dispatch(setSaveStringGridSelect(rowSelectedString));
    dispatch(setSaveMultipleStationSelected(arrayToBlob));
  });
  // ================= useEffect ======================

  // when the value of btnExport changes therefore when clicking on the exportAsCSV
  //  button launch of the API function of the grid to export the selected data
  // if nothing selected all table is export
  useEffect(() => {
    if (btnExport === true) {
      if (multipleStationsSelected.length === 0 || multipleStationsSelected[0] == null) {
        gridRef.current.api.exportDataAsCsv();
      }
      else if (multipleStationsSelected.length > 0 && multipleStationsSelected[0] !== null) {
        gridRef.current.api.exportDataAsCsv({ onlySelected: true });
      }
      dispatch(setBtnExport(false));
    }
  }, [btnExport]);

  // launch of data formatting of each column when a new station is
  // selected or deselected to update the grid data
  useEffect(() => {
    rowsOfMultipleDataGrid();
  }, [dataPercent]);

  // when a grid line is selected display of the corresponding timeseries graphs
  const onRowSelected = useCallback(
    (event) => {
      if (event.node.isSelected()) {
        dispatch(setTimeseries(event.node.data.marker));
      }
      else if (event.node.isSelected() === false) {
        if (event.api.getSelectedNodes().length > 0) {
          dispatch(setTimeseries(event.api.getSelectedNodes()[0].data.marker));
        }
        else {
          dispatch(setTimeseries(''));
        }
      }
    },
    [window],
  );

  return (
    <>
      <div className="ag-theme-alpine dataGrid">
        <div className="metadataGrid--height">
          <AgGridReact
            ref={gridRef}
            className="multipleDataGrid"
            columnDefs={columns}
            rowData={multipleGridRows}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            columnTypes={columnTypes}
            onSelectionChanged={onSelectionChanged}
            onRowSelected={onRowSelected}
          />
        </div>
      </div>
      <div className="totalItems">Total Stations: {multipleGridRows.length} {((multipleStationsSelected.length > 0) && (multipleStationsSelected[0] !== undefined)) && ` (selected station(s): ${multipleStationsSelected.length})`}</div>
    </>

  );
}

export default MultiplemetadataGrid;
